import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  label: string;
}

function pageWindow(page: number, totalPages: number) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  const pages = new Set([1, totalPages, page - 1, page, page + 1]);
  return [...pages].filter((item) => item >= 1 && item <= totalPages).sort((a, b) => a - b);
}

export function Pagination({ page, pageSize, totalItems, onPageChange, label }: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = totalItems ? (safePage - 1) * pageSize + 1 : 0;
  const end = Math.min(totalItems, safePage * pageSize);
  const pages = pageWindow(safePage, totalPages);

  return (
    <nav className="pagination" aria-label={label}>
      <span className="pagination-summary">
        显示 {start}–{end} / 共 {totalItems.toLocaleString("zh-CN")} 条
      </span>
      <div className="pagination-controls">
        <button
          className="pagination-arrow"
          aria-label="上一页"
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
        >
          <ChevronLeft size={15} />
        </button>
        {pages.map((item, index) => {
          const previous = pages[index - 1];
          return (
            <span className="pagination-slot" key={item}>
              {previous && item - previous > 1 && <i aria-hidden="true">…</i>}
              <button
                className={item === safePage ? "is-active" : ""}
                aria-label={`第 ${item} 页`}
                aria-current={item === safePage ? "page" : undefined}
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            </span>
          );
        })}
        <button
          className="pagination-arrow"
          aria-label="下一页"
          disabled={safePage >= totalPages}
          onClick={() => onPageChange(safePage + 1)}
        >
          <ChevronRight size={15} />
        </button>
      </div>
    </nav>
  );
}
